import axios from "axios";

import { fullAPIURL } from "../utils/requests";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import { format } from "date-fns";
import { Nullable } from "../types/nullable";

const calendarAxios = axios.create({
  baseURL: `${fullAPIURL}/v2/calendar`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

calendarAxios.interceptors.request.use(requestAccessTokenInterceptor);
calendarAxios.interceptors.request.use(refreshAccessTokenInterceptor);
calendarAxios.interceptors.response.use(responseStatusInterceptor);

export interface CheckInParams {
  clientUuid: string;
  userUuid?: string;
  registrationUuid: string;
  source: string;
  shouldOverrideCheckinRules: boolean;
  isStaff?: boolean;
  isCoaching?: boolean;
  isAssisting?: boolean;
}

export async function checkIn(params: CheckInParams): Promise<void> {
  const url = `/checkin`;
  await calendarAxios.post(url, {
    client: params.clientUuid,
    user: params.userUuid,
    registration: params.registrationUuid,
    source: params.source,
    shouldOverrideCheckinRules: params.shouldOverrideCheckinRules,
    is_staff: params.isStaff,
    is_coaching: params.isCoaching,
    is_assisting: params.isAssisting,
  });
}

export async function getCalendarItem(uuid: string): Promise<CalendarItems> {
  const { data } = await calendarAxios.get<unknown, { data: CalendarItems }>(
    `/items/${uuid}`,
  );
  return data;
}

export async function getCalendarItems(
  startTimestamp: number,
  endTimestamp: number,
  staffParam?: "coach_uuid" | "assistant_coach_uuid",
  staffUuid?: string,
): Promise<CalendarItems[]> {
  const url = `/item`;
  const response = await calendarAxios.get(url, {
    params: {
      "start_timestamp[lt]": endTimestamp,
      "end_timestamp[gt]": startTimestamp,
      ...(staffUuid ? { [staffParam]: staffUuid } : {}),
    },
  });

  return response.data.data || [];
}

export interface PaginatedCalendarItems {
  data: MinimalCalendarItem[];
  hasMore: boolean;
  page: number;
  limit: number;
}

export async function getSinglePageCalendarItems(
  options: { page?: number; limit?: number } = {},
): Promise<PaginatedCalendarItems> {
  const url = `/items/range`;
  const response = await calendarAxios.get(url, {
    params: {
      date: format(new Date(), "yyyy-MM-dd"),
      page: options.page ?? 1,
      limit: options.limit ?? 50,
    },
  });
  return response.data.data;
}

// Because this API is paginated, we need to fetch all pages and combine the results
export async function getAllTodaysCalendarItems(): Promise<
  MinimalCalendarItem[]
> {
  let currentPage = 1;
  const limit = 50;
  const allItems: MinimalCalendarItem[] = [];
  let hasMore = true;

  while (hasMore) {
    const response = await getSinglePageCalendarItems({
      page: currentPage,
      limit,
    });
    allItems.push(...response.data);
    hasMore = response.hasMore;
    currentPage++;
  }

  return allItems;
}

export function createInvitations({
  calendarItemUuid,
  userUuids,
}: {
  calendarItemUuid: string;
  userUuids: string[];
}) {
  return calendarAxios.post<unknown, { uuid: string }[]>(
    `items/${calendarItemUuid}/invitations/`,
    {
      userUuids,
    },
  );
}

export async function revokeInvitation(uuid: string) {
  const url = `/invitations/${uuid}`;
  await calendarAxios.delete(url);
}

export type CalendarItemsType = {
  id: number;
  uuid: string;
  client_uuid: Nullable<string>;
  name: string;
  description: Nullable<string>;
  color: string;
  free_session: boolean;
  max_registrations: number;
  active: boolean;
  deleted: boolean;
};

export type StartDate = {
  _dateFormat: {
    formatString: string;
    regex: Record<string, unknown>;
  };
  _dateString: string;
};

export type CalendarItems = {
  id: number;
  attendanceCap: number;
  isActive: boolean;
  isDeleted: boolean;
  start_datetime: string;
  calendar_type_id: number;
  calendar_session_type_id: number;
  uuid: string;
  client_id: number;
  client_uuid: string;
  title: string;
  end_datetime: string;
  year: number;
  doy: number;
  template_id: number;
  is_template: boolean;
  location_id: number;
  location_uuid: Nullable<string>;
  recurrence_id: Nullable<string>;
  coach_id: number;
  coach_uuid: Nullable<string>;
  assistant_coach_id: number;
  assistant_coach_uuid: Nullable<string>;
  tax_rate_id: number;
  description: string;
  is_recurring: boolean;
  recurring_day_of_week: string;
  start_timestamp: number;
  end_timestamp: number;
  registration_start_offset: number;
  registration_end_offset: number;
  duration: number;
  recurrence_end_timestamp: number;
  is_all_day: boolean;
  price: string;
  start_timezone: string;
  end_timezone: string;
  allow_registration: boolean;
  require_registration: boolean;
  allow_waitlist: boolean;
  mailchimp_mergevar: Nullable<string>;
  show_waiver: boolean;
  redirect_url: Nullable<string>;
  is_public: boolean;
  create_timestamp: number;
  update_timestamp: number;
  last_cron_timestamp: number;
  registration_late_cancel_offset: number;
  series: string;
  series_uuid: string;
  type: CalendarItemsType;
  startDate: StartDate;
};

export type MinimalCalendarItem = {
  uuid: string;
  coachUuid: Nullable<string>;
  assistantCoachUuid: Nullable<string>;
  clientUuid: Nullable<string>;
  title: Nullable<string>;
  type: Nullable<string>;
  classTypeName: Nullable<string>;
  locationUuid: Nullable<string>;
  start: number;
  end: number;
  startDateTime: Nullable<string>;
  endDateTime: Nullable<string>;
  price: string; // format: "0.00"
};
